import { motion } from 'framer-motion';
import {
  BarChart3,
  CircleX,
  FileText,
  LayoutDashboard,
  LogOut,
  // MessageSquareReply, // Refund & Pengembalian is hidden for now
  Package,
  Undo2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
// import dropdownArrow from '@/assets/icon/dropdown-arrow-grey.svg';
import dropdownArrow from '@/assets/icon/dropdown-arrow.svg';
import logoutIcon from '@/assets/icon/logout-icon.svg';
import Logo from '@/assets/store/icon/logo.svg';
import ModalProfileManagement from '@/domains/profile-management/components/ModalProfileManagement/ModalProfileManagement';
import { cn } from '@/lib/utils';
import LogoutConfirmModal from '@/shared/components/layout/LogoutConfirmModal/LogoutConfirmModal';
import UserProfile from '@/shared/components/layout/UserProfile/UserProfile';
import Button from '@/shared/components/ui/Button/Button';
import ThemeToggle from '@/shared/components/ui/ThemeToggle/ThemeToggle';
import { URLS } from '@/shared/config/urls';
import i18n from '@/shared/utils/i18n';
import Common from '@/shared/utils/standard';
import classes from './Sidebar.module.css';
import type { SidebarProps } from './Sidebar.types';

const Sidebar = ({
  userInfo,
  commonData,
  navbarList,
  preferenceData,
  isOpen = false,
  onClose,
}: SidebarProps): JSX.Element => {
  const navigate = useNavigate();
  const [time, setTime] = useState('');
  const [date, setDate] = useState('');
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [language, setLanguage] = useState('');

  useEffect(() => {
    const langSource = Common.getDTPLangSource(i18n.language as 'kor' | 'eng' | 'ind');
    setLanguage(i18n.language.toLocaleUpperCase());
    const updateDateTime = () => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const seconds = now.getSeconds().toString().padStart(2, '0');

      const dayName = langSource?.days[now.getDay()] ?? '';
      const monthName = langSource?.months[now.getMonth()] ?? '';
      const day = now.getDate();
      const year = now.getFullYear();

      setTime(`${hours}:${minutes}:${seconds}`);
      setDate(`${dayName}, ${monthName} ${day} ${year}`);
    };

    updateDateTime();
    const intervalId = setInterval(updateDateTime, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const programOrder = [
    'DASHBOARD',
    'SYSTEM',
    'ASSET',
    'SITE-MANAGEMENT',
    'STATISTICS',
    'SCENARIO',
    'LOG',
  ];

  const categories = navbarList

    ?.sort((a, b) => {
      const indexA = programOrder.indexOf(a.program_id);
      const indexB = programOrder.indexOf(b.program_id);

      // programOrder에 없는 항목은 -1 → Infinity로 변환하여 뒤로 보냄
      const orderA = indexA === -1 ? Infinity : indexA;
      const orderB = indexB === -1 ? Infinity : indexB;

      return orderA - orderB;
    })
    ?.map((item) => ({
      name: item.program_id,
      label: item.lang,
      path: item.url || item.urls?.[0],
    }));

  const links = navbarList?.flatMap(
    (item) =>
      item.child_program_ids?.map((childId, index) => ({
        id: childId,
        label: item.langs?.[index],
        path: item.urls?.[index],
        category: item.program_id,
      })) ?? []
  );
  console.log('categories', categories);
  console.log('links', links);

  const dataHeader = [
    {
      name: 'Beranda',
      link: '/dashboard/home',
      icon: LayoutDashboard,
      activePaths: ['/dashboard/home'],
    },
    {
      name: 'Riwayat Transaksi',
      link: '/dashboard/history',
      icon: FileText,
      activePaths: ['/dashboard/history', '/dashboard/history-detail'],
    },
    {
      name: 'Pengaturan Produk',
      link: '/dashboard/product',
      icon: Package,
      activePaths: ['/dashboard/product', '/dashboard/product-detail'],
    },
    {
      name: 'Affiliate',
      link: '/dashboard/affiliate',
      icon: BarChart3,
      activePaths: ['/dashboard/affiliate', '/dashboard/affiliate-detail'],
    },
    // Refund & Pengembalian is hidden for now
    // {
    //   name: 'Refund & Pengembalian',
    //   link: '/dashboard/refund',
    //   icon: MessageSquareReply,
    //   activePaths: ['/dashboard/refund', '/dashboard/refund-detail'],
    // },
  ];

  const [openMenu, setOpenMenu] = useState('');
  const [isLinksReady, setIsLinksReady] = useState(false);
  const linksRef = useRef<
    { id: string; label: string | undefined; path: string | undefined; category: string }[]
  >([]);

  const toggleVisibility = (menu: string) => {
    setOpenMenu((prevMenu) => (prevMenu === menu ? '' : menu));
  };

  // const toggleVisibility = () => {
  //   setIsVisible((prev) => !prev);
  // };
  const isLinkActive = (linkPath: string) => {
    return location.pathname.includes(linkPath);
  };

  const [logoutOpen, setLogoutOpen] = useState(false);

  useEffect(() => {
    linksRef.current = links ?? [];
    setIsLinksReady(!!links && links.length > 0);
  }, [links]);

  useEffect(() => {
    if (isLinksReady) {
      const currentPath = location.pathname;
      const matchedLink = linksRef.current.find((link) => link.path === currentPath);

      if (matchedLink) {
        setOpenMenu(matchedLink.category);
      }
    }
  }, [location.pathname, isLinksReady]);

  // Mobile drawer: tutup saat pindah halaman. Sengaja hanya bergantung pada
  // pathname; jika isOpen ikut jadi dependency, drawer langsung tertutup
  // begitu dibuka.
  // biome-ignore lint/correctness/useExhaustiveDependencies: close only on route change, not when isOpen/onClose change
  useEffect(() => {
    if (isOpen) onClose?.();
  }, [location.pathname]);

  // Mobile drawer
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      {/* overlay */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 md:hidden',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <section
        className={cn(
          'fixed inset-y-0 left-0 z-50 h-screen w-[300px] max-w-[85vw] shrink-0 border-r border-blue-cs-200 bg-white transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          'md:sticky md:top-0 md:z-auto md:max-w-none md:translate-x-0 md:transition-none'
        )}
        aria-label="Sidebar"
      >
        <div className="h-full overflow-y-auto px-5 py-6 gap-6 flex flex-col">
          <div className="flex items-center justify-between md:block">
            <button
              type="button"
              onClick={onClose}
              aria-label="Tutup menu"
              className="md:hidden text-black p-1 -ml-1"
            >
              <CircleX size={28} strokeWidth={1.5} />
            </button>
            <figure className="h-fit w-fit md:w-full">
              <img
                src={Logo}
                alt="rumah kebijaksanaan"
                loading="eager"
                className="h-7 w-auto md:h-auto"
              />
            </figure>
          </div>
          <Button
            className="btn-fill-white w-full bg-[#F6F6F6]! items-center "
            onClick={() => navigate('store/home')}
          >
            <Undo2 />
            <span className="text-black title2medium min-w-0 truncate">
              Kembali ke halaman utama
            </span>
          </Button>
          <div className=" divider h-[0.5px] w-full bg-blue-cs-200"></div>
          <div className="gap-3 flex-2 flex flex-col overflow-auto ">
            <p className="body1 text-black">Menu Utama</p>
            {dataHeader.map((item) => {
              const isActive = item.activePaths.some((path) => location.pathname.startsWith(path));
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.name}
                  to={item.link}
                  className={cn(
                    'title2medium px-4 border py-2 rounded-[8px] capitalize transition-colors flex items-center gap-3',
                    isActive
                      ? 'bg-text-blue-12 text-text-blue-200 border-text-blue-200'
                      : 'text-text-grey border-transparent'
                  )}
                >
                  <Icon size={18} className=" shrink-0" />
                  <span>{item.name}</span>
                </NavLink>
              );
            })}
          </div>

          <div className="flex-auto pb-10 items-end flex md:hidden">
            <div className="items-center  w-full inline-flex gap-3 border-t border-blue-cs-200 pt-6 justify-between ">
              <figure className="inline-flex gap-3 items-center w-fit">
                <UserProfile />
              </figure>

              <button type="button" onClick={() => setLogoutOpen(true)} aria-label="Logout">
                <LogOut color="red" size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>
      {/* Outside the section: its transform would trap the fixed modal inside the drawer */}
      <LogoutConfirmModal open={logoutOpen} onClose={() => setLogoutOpen(false)} />
    </>
  );
};

export default Sidebar;
